<template>
  <!-- eslint-disable tailwindcss/no-custom-classname -->

  <section ref="wrapper">
    <slot name="customTitle"></slot>
    <BlockTitle :subtitle :title />

    <CategorySelector
      v-if="categoryOptions?.length > 1"
      :category-options
      class="mb-8"
      :selected-category
      @category-selected="updateCategory"
    />

    <section class="flex max-h-[1000px] w-full justify-center md:h-screen">
      <section class="h-full w-full max-w-[1440px]">
        <div
          class="bg-action-default-mid relative h-full w-full overflow-hidden rounded-[32px] md:block"
        >
          <button
            class="z-2 absolute left-28 top-1/2 hidden translate-y-[-50%] cursor-pointer flex-col items-center md:flex"
            @mousedown.prevent="onMouseDownLeft"
            @mouseup.prevent="onMouseUp"
          >
            <div
              class="bg-surface-default-low rounded-full mb-4 flex h-40 w-40 items-center justify-center"
            >
              <IconTrade class="scale-x-[-1]" />
            </div>
            <p class="body-2">
              {{ i18n(translations.turnLeftLabel) }}
            </p>
          </button>
          <div
            class="z-2 absolute right-28 top-1/2 hidden translate-y-[-50%] md:block"
          >
            <div class="flex translate-y-[-25%] flex-col items-center">
              <button
                class="mb-12 flex cursor-pointer flex-col items-center"
                @click="onToggleZoom"
              >
                <div
                  class="bg-surface-default-low rounded-full mb-4 flex h-40 w-40 items-center justify-center"
                  :class="isZooming ? 'border-1 border-static-default-hi' : ''"
                >
                  <IconGlasses />
                </div>
                <p class="body-2">
                  {{ i18n(zoomLabel) }}
                </p>
              </button>
              <button
                class="flex cursor-pointer flex-col items-center"
                @mousedown.prevent="onMouseDownRight"
                @mouseup.prevent="onMouseUp"
              >
                <div
                  class="bg-surface-default-low rounded-full mb-4 flex h-40 w-40 items-center justify-center"
                >
                  <IconTrade />
                </div>
                <p class="body-2">
                  {{ i18n(translations.turnRightLabel) }}
                </p>
              </button>
            </div>
          </div>
          <div
            class="relative flex h-full w-full flex-col items-center overflow-hidden"
          >
            <div class="z-2 flex w-full flex-col py-24 md:items-center">
              <nav class="hide-scrollbar flex overflow-x-auto px-24 md:px-0">
                <div class="flex min-w-[420px] flex-nowrap">
                  <RevPicker
                    v-for="(picker, index) in pickers"
                    :key="picker.grade"
                    class="mr-4 w-[7.5rem] min-w-[7.5rem]"
                    :label="i18n(picker.label)"
                    :model-value="index === selectedGrade"
                    :tracking="{
                      ...tracking,
                      name: '360_block',
                      value: picker.grade,
                    }"
                    @update:model-value="onGradeClick(index)"
                  />
                </div>
              </nav>
            </div>
            <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
            <div
              aria-label="Interactive canvas"
              class="relative flex h-full w-full flex-1 cursor-grab justify-center"
              :class="{ 'cursor-grabbing': isDragging }"
              role="application"
              @mousedown.prevent="onMousedownCanvasWrapper"
              @mousemove.prevent="onMousemoveCanvasWrapper"
              @mouseup.prevent="onMouseupCanvasWrapper"
              @touchend.prevent="onTouchendCanvasWrapper"
              @touchmove.prevent="onTouchmoveCanvasWrapper"
              @touchstart.prevent="onTouchstartCanvasWrapper"
            >
              <!-- eslint-disable-next-line -->
              <div
                ref="zoomContainer"
                class="aspect-square h-auto w-full md:absolute md:top-1/2 md:h-full md:w-auto md:-translate-y-1/2"
                @mouseenter.prevent="onMouseEnterCanvas"
                @mouseleave.prevent="onMouseLeaveCanvas"
                @mousemove.prevent="onMouseMoveCanvas"
                @touchend.prevent="onTouchEndCanvas"
                @touchmove.prevent="onTouchmoveCanvas"
                @touchstart.prevent="onTouchStartCanvas"
              >
                <ImagesSequence
                  ref="imagesSequence"
                  :activeSequence="selectedGrade"
                  format="webp"
                  :imagesCount="120"
                  :sequencesCount="pickers.length"
                />
              </div>
            </div>
            <div
              class="z-2 flex w-full flex-col pb-16 pt-20 md:items-center md:py-24"
            >
              <div class="flex self-center md:hidden">
                <button
                  class="mr-24 flex flex-col items-center"
                  @pointerdown.prevent="onMouseDownLeft"
                  @pointerup.prevent="onMouseUp"
                >
                  <div
                    class="bg-surface-default-low rounded-full mb-4 flex h-40 w-40 items-center justify-center"
                  >
                    <IconTrade class="scale-x-[-1]" />
                  </div>
                  <p class="body-2">
                    {{ i18n(translations.turnLeftLabel) }}
                  </p>
                </button>
                <button
                  class="flex cursor-pointer flex-col items-center"
                  @click="onToggleZoom"
                >
                  <div
                    class="bg-surface-default-low rounded-full mb-4 flex h-40 w-40 items-center justify-center"
                    :class="
                      isZooming ? 'border-1 border-static-default-hi' : ''
                    "
                  >
                    <IconGlasses />
                  </div>
                  <p class="body-2">
                    {{ i18n(zoomLabel) }}
                  </p>
                </button>
              </div>
            </div>
            <div
              class="hide-scrollbar z-2 mb-32 mt-20 w-full overflow-x-auto px-28 md:flex md:justify-center"
            >
              <ul class="flex w-max flex-nowrap md:w-full md:justify-center">
                <li
                  v-for="(item, index) in productDetailsFiltered"
                  :key="index"
                  class="mr-24 w-[16.5rem] last:mr-0 md:w-1/4"
                >
                  <component :is="item.icon" />
                  <p class="text-static-default-hi text-body-1-bold my-8">
                    {{ i18n(item.title) }}
                  </p>
                  <div class="hide-scrollbar md:h-[6rem] md:overflow-y-scroll">
                    <p class="text-static-default-low text-body-2">
                      {{
                        i18n(
                          item.text[selectedCategory][
                            pickers[selectedGrade].grade
                          ],
                        )
                      }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </section>

    <div v-if="button" class="mt-24 flex w-full justify-center">
      <CmsButton
        data-test="product-grades-cta-button"
        v-bind="button"
        full-width="fixed"
        :tracking
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import type { ProductCategory } from '@backmarket/http-api/src/api-specs-content/models/product-category'
import type { ProductGradesProps } from '@backmarket/http-api/src/api-specs-content/models/product-grades'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import {
  Breakpoint,
  getCurrentBreakpoint,
} from '@backmarket/utils/dom/getCurrentBreakpoint'
import { clamp } from '@backmarket/utils/math/clamp'
import { RevPicker } from '@ds/components/Picker'
import { IconGlasses } from '@ds/icons/IconGlasses'
import { IconTrade } from '@ds/icons/IconTrade'
import { useIntersectionObserver } from '@vueuse/core'

import type { ContentBlockProps } from '../../models/content-block'
import CategorySelector from '../../shared-components/AnimatedProductCheckpoints/CategorySelector.vue'
import BlockTitle from '../../shared-components/BlockTitle/BlockTitle.vue'
import CmsButton from '../Button/Button.vue'

import ImagesSequence from './ImagesSequence/ImagesSequence.vue'
import {
  GRADES,
  type Grade,
  buildPickers,
  categoriesData,
  getProductDetails,
} from './ProductGrades.data'
import translations from './ProductGrades.translations'
import { useRaf } from './useRaf'

const {
  categories,
  subtitle,
  title,
  tracking = {},
} = defineProps<ProductGradesProps & ContentBlockProps>()

const selectedCategory = ref(categories[0].name)
const path = computed(
  () => categoriesData[selectedCategory.value].packshotsPath,
)

const { trackClick, trackBlockImpression } = useTracking()
const i18n = useI18n()

// this is not reactive so there's no need to use a computed
const categoryOptions: Array<{ category: ProductCategory; label: string }> =
  categories.map((category) => {
    return {
      category: category.name,
      label: i18n(translations[category.name]),
    }
  })

const pickers = computed(() => buildPickers(selectedCategory.value))

const productDetailsFiltered = computed(() =>
  getProductDetails(selectedCategory.value),
)

function getInitialGradeIndex() {
  const { query } = useRoute()
  if (!query.grade || typeof query.grade !== 'string') return 2

  const gradeIndex = GRADES.indexOf(query.grade as unknown as Grade)

  return gradeIndex !== -1 ? gradeIndex : 2
}

const selectedGrade = ref(getInitialGradeIndex())

const wrapper = useTemplateRef<HTMLDivElement>('wrapper')
const zoomContainer = useTemplateRef<HTMLDivElement>('zoomContainer')

let hasInteracted = false

const imagesSequence = useTemplateRef('imagesSequence')
const hasTriggeredIntersection = ref(false)

// drag
const isDragging = ref(false)
const isZooming = ref(true)

let progressAtDragStart = 0
const drag = {
  start: 0,
  distance: 0,
}

let progress = 0
let progressSpeed = 0

const isMobileVersion = computed(() => getCurrentBreakpoint() < Breakpoint.MD)

const targetZoomSize = computed(() => (isMobileVersion.value ? 110 : 150))
const zoomIntensity = computed(() => (isMobileVersion.value ? 4 : 2.75))

const zoomLabel = computed(() =>
  isZooming.value ? translations.stopZoomLabel : translations.startZoomLabel,
)

const zoomSize = ref(0)

const zoomContainerSizes = {
  width: 0,
  height: 0,
}

function loadGradeImages(gradeIndex: number) {
  imagesSequence.value?.loadImagesAtInterval(
    getGradePath(gradeIndex),
    gradeIndex,
    undefined,
    2,
  )
}

// fade animation
const fadeInDuration = 400
const fadeOutDuration = 200
const isAnimatingFadeIn = ref(false)
const canvasAlpha = ref(1)
/**
 *
 * Path utils
 */

function getGradePath(selectedGradeIndex: number): string {
  return `${path.value}${pickers.value[selectedGradeIndex].grade}/`
}

async function updateCategory(category: ProductCategory) {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  fadeOutCanvas(fadeOutDuration, async () => {
    imagesSequence.value?.clearImages()
    selectedCategory.value = category
    selectedGrade.value = 0

    await imagesSequence.value?.loadImagesAtInterval(
      getGradePath(selectedGrade.value),
      selectedGrade.value,
      undefined,
      2,
    )

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    fadeInCanvas(fadeInDuration)
  })
}

/**
 *
 * Tracking
 */

function setHasInteracted() {
  if (!hasInteracted) {
    hasInteracted = true

    trackClick({
      ...tracking,
      name: '360_block',
      value: '360_view',
    })
  }
}

/**
 *
 * Drag and touch functions
 */

function checkProgressLimits(posX: number) {
  if (progress === 1) {
    progress = 0
    progressAtDragStart = 0
    drag.start = posX
  } else if (progress === 0) {
    progress = 1
    progressAtDragStart = 1
    drag.start = posX
  }
}

/**
 *
 * Zoom utils
 */
function calcZoomContainerSizes() {
  if (!zoomContainer.value) return

  const { width, height } = zoomContainer.value.getBoundingClientRect()
  zoomContainerSizes.width = width
  zoomContainerSizes.height = height
}

// xPos, yPos are the cursor position in the page
function drawMagnifyingGlass(xPos: number, yPos: number) {
  imagesSequence.value?.addZoomEffect({
    activeSequenceIndex: selectedGrade.value,
    progress,
    xPos,
    yPos,
    zoomIntensity: zoomIntensity.value,
    zoomSize: zoomSize.value,
  })
}

/**
 *
 * Alpha utils
 */

function easeInOutQuad(t: number) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

function fadeOutCanvas(duration: number, callback: () => void) {
  if (!imagesSequence.value) return
  isAnimatingFadeIn.value = true

  const start = performance.now()
  const initialAlpha = 1
  const finalAlpha = 0
  let animationFrameId: number

  const fade = (time: number) => {
    const elapsed = time - start
    const progressAnim = Math.min(elapsed / duration, 1)
    const easedProgress = easeInOutQuad(progressAnim)
    const alpha = initialAlpha + (finalAlpha - initialAlpha) * easedProgress

    canvasAlpha.value = alpha
    imagesSequence.value?.updateSequence(
      progress,
      selectedGrade.value,
      canvasAlpha.value,
    )

    if (progressAnim < 1) {
      animationFrameId = requestAnimationFrame(fade)
    } else {
      cancelAnimationFrame(animationFrameId)
      callback()
    }
  }

  animationFrameId = requestAnimationFrame(fade)
}

function fadeInCanvas(duration: number) {
  if (!imagesSequence.value) return
  isAnimatingFadeIn.value = true

  const start = performance.now()
  const initialAlpha = 0
  const finalAlpha = 1
  let animationFrameId: number

  const fade = (time: number) => {
    const elapsed = time - start
    const progressAnim = Math.min(elapsed / duration, 1)
    const easedProgress = easeInOutQuad(progressAnim)
    const alpha = initialAlpha + (finalAlpha - initialAlpha) * easedProgress

    canvasAlpha.value = alpha
    imagesSequence.value?.updateSequence(
      progress,
      selectedGrade.value,
      canvasAlpha.value,
    )

    if (progressAnim < 1) {
      animationFrameId = requestAnimationFrame(fade)
    } else {
      isAnimatingFadeIn.value = false
      cancelAnimationFrame(animationFrameId)
    }
  }

  animationFrameId = requestAnimationFrame(fade)
}

/**
 *
 * Events
 *
 */

function onGradeClick(index: number) {
  fadeOutCanvas(fadeOutDuration, () => {
    selectedGrade.value = index
    fadeInCanvas(fadeInDuration)
  })
}

function onMousedownCanvasWrapper(e: MouseEvent) {
  setHasInteracted()

  isDragging.value = true
  drag.start = e.clientX
  progressAtDragStart = progress
}

function onMousemoveCanvasWrapper(e: MouseEvent) {
  if (!isDragging.value) return

  drag.distance = e.clientX - drag.start
  progress = progressAtDragStart + (drag.distance / 1000) * -1
  progress = clamp(progress, 0, 1)
  checkProgressLimits(e.clientX)

  imagesSequence.value?.updateSequence(
    progress,
    selectedGrade.value,
    canvasAlpha.value,
  )
}

function onMouseupCanvasWrapper() {
  isDragging.value = false
  drag.start = 0
  drag.distance = 0
  progressAtDragStart = 0
}

function onMouseDownLeft() {
  setHasInteracted()

  progressSpeed = 0.005
}

function onMouseDownRight() {
  setHasInteracted()

  progressSpeed = -0.005
}

function onMouseUp() {
  progressSpeed = 0
}

function onTouchstartCanvasWrapper(e: TouchEvent) {
  if (isZooming.value) return
  setHasInteracted()

  isDragging.value = true
  drag.start = e.touches[0].clientX
  progressAtDragStart = progress
}

function onTouchmoveCanvasWrapper(e: TouchEvent) {
  if (!isDragging.value || e.touches.length === 0 || isZooming.value) return
  drag.distance = e.touches[0].clientX - drag.start
  progress = progressAtDragStart + (drag.distance / 500) * -1
  progress = clamp(progress, 0, 1)

  checkProgressLimits(e.touches[0].clientX)

  imagesSequence.value?.updateSequence(
    progress,
    selectedGrade.value,
    canvasAlpha.value,
  )
}

function onTouchendCanvasWrapper() {
  isDragging.value = false
  drag.start = 0
  drag.distance = 0
  progressAtDragStart = 0
}

function onToggleZoom() {
  isZooming.value = !isZooming.value
}

function onMouseEnterCanvas(e: MouseEvent) {
  if (!isZooming.value) return
  zoomSize.value = targetZoomSize.value

  setTimeout(() => drawMagnifyingGlass(e.pageX, e.pageY))
}

function onMouseLeaveCanvas(e: MouseEvent) {
  if (!isZooming.value) return
  zoomSize.value = 0

  setTimeout(() => drawMagnifyingGlass(e.pageX, e.pageY))
}

function onMouseMoveCanvas(e: MouseEvent) {
  if (!isZooming.value) return
  setTimeout(() => drawMagnifyingGlass(e.pageX, e.pageY))
}

function onTouchStartCanvas(e: TouchEvent) {
  if (!isZooming.value) return
  zoomSize.value = targetZoomSize.value

  setTimeout(() =>
    drawMagnifyingGlass(
      e.touches[0].pageX,
      e.touches[0].pageY - zoomSize.value,
    ),
  )
}

function onTouchEndCanvas() {
  if (!isZooming.value) return
  zoomSize.value = 0

  setTimeout(() => drawMagnifyingGlass(0, 0))
}

function onTouchmoveCanvas(e: TouchEvent) {
  if (!isZooming.value) return
  setTimeout(() =>
    drawMagnifyingGlass(
      e.touches[0].pageX,
      e.touches[0].pageY - zoomSize.value,
    ),
  )
}

function onResize() {
  imagesSequence.value?.onResize()
  imagesSequence.value?.updateSequence(
    progress,
    selectedGrade.value,
    canvasAlpha.value,
  )
  calcZoomContainerSizes()
}

function onTick() {
  if (progressSpeed !== 0) {
    progress += progressSpeed
    progress = clamp(progress, 0, 1)
    if (progress === 1) {
      progress = 0
    } else if (progress === 0) {
      progress = 1
    }
    imagesSequence.value?.updateSequence(
      progress,
      selectedGrade.value,
      canvasAlpha.value,
    )
  }
}

/**
 *
 * Intersection observer
 */

const { stop } = useIntersectionObserver(
  wrapper,
  async ([{ isIntersecting }]) => {
    if (isIntersecting) {
      hasTriggeredIntersection.value = true

      trackBlockImpression({
        ...tracking,
        name: '360_block',
        value: '360_view',
      })

      // we load the images for the current grade
      await imagesSequence.value?.loadImagesAtInterval(
        getGradePath(selectedGrade.value),
        selectedGrade.value,
        undefined,
        2,
      )

      // then we draw the images for the current grade
      imagesSequence.value?.updateSequence(
        progress,
        selectedGrade.value,
        canvasAlpha.value,
      )
    }
  },
)

/**
 *
 * Listeners
 *
 */
function addEvents(): void {
  window.addEventListener('resize', onResize)
}

function removeEvents(): void {
  window.removeEventListener('resize', onResize)
}

/**
 *
 * Hooks
 */

onMounted(() => {
  if (isMobileVersion.value) {
    zoomSize.value = 90
  }

  addEvents()
  calcZoomContainerSizes()
})

onBeforeUnmount(() => {
  stop()
})

onUnmounted(() => {
  removeEvents()
})

useRaf(onTick)

watch(
  () => selectedGrade.value,
  () => {
    nextTick(async () => {
      const grade = selectedGrade.value

      await loadGradeImages(grade)

      imagesSequence.value?.updateSequence(
        progress,
        selectedGrade.value,
        canvasAlpha.value,
      )
    })
  },
)

watch(
  () => selectedCategory.value,
  () => {
    nextTick(() => {
      const currentPath = getGradePath(selectedGrade.value)
      imagesSequence.value?.loadImagesAtInterval(
        currentPath,
        selectedGrade.value,
        undefined,
        2,
      )
    })
  },
)

watch(hasTriggeredIntersection, () => {
  if (hasTriggeredIntersection.value) {
    stop()
  }
})

watch(
  () => isZooming.value,
  () => {
    if (!isZooming.value) {
      imagesSequence.value?.updateSequence(
        progress,
        selectedGrade.value,
        canvasAlpha.value,
      )
    }
  },
)
</script>

<style lang="css" scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
